import { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { HttpError } from '@frontend/fetch';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  CheckboxField,
  Chip,
  currency,
  CustomChangeEvent,
  DateField,
  FieldChangeEvent,
  Heading,
  normalizeChangeHandler,
  PopoverMenu,
  PopoverMenuItem,
  Text,
  useAlert,
  useForm,
  usePopoverMenu,
} from '@frontend/design-system';
import { useCareCreditPrefillApplicationQueries } from '../../../hooks/use-care-credit-prefill-application-queries';
import { useCareCreditTranslations } from '../../../hooks/use-care-credit-translations';
import { getHousingTypeLabel, getIdTypeLabel } from '../../../utils/form-option-labels';
import { EnrollmentBaseStep } from '../atoms/step/enrollment-base-step';
import { EnrollmentStepPerformanceWrapper } from '../atoms/step/enrollment-step-performance-wrapper';
import { useCareCreditEnrollmentContext } from '../context/care-credit-enrollment.context';
import { CareCreditEnrollmentMultiSteps, EnrollmentConfirmationData } from '../enrollment-multi-step.types';

const CURRENT_STEP: CareCreditEnrollmentMultiSteps = 'application-details-submit';
// const PREV_STEP: CareCreditEnrollmentMultiSteps = 'applicant-pre-qualified-confirm';
const NEXT_STEP: CareCreditEnrollmentMultiSteps = 'application-confirmation-result';

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
  `,
  form: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
  row: css`
    display: flex;
    gap: ${theme.spacing(2)};

    @media (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: column;
    }

    & > * {
      width: calc(33.3% - ${theme.spacing(2)});
      flex-shrink: 0;
      white-space: pre-line;

      @media (max-width: ${breakpoints.xsmall.max}px) {
        width: 100%;
      }
    }
  `,

  signatureBlock: css`
    border: solid 1px ${theme.colors.neutral30};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(2)};
  `,
  signatureBlockInner: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: space-between;
  `,
  signatureBlockChipContent: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `,
  disclaimerBlock: css`
    background: ${theme.colors.neutral5};
    border: solid 1px ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
    padding: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  `,
  dateInput: css`
    @media (min-width: ${breakpoints.small.min}px) {
      width: 50%;
    }
  `,
  divider: css`
    border-top: solid 1px ${theme.colors.neutral20};
    margin: ${theme.spacing(2)} 0;
  `,

  stepItem: css`
    margin-bottom: ${theme.spacing(2)};
  `,
  stepItemLabel: css`
    color: ${theme.colors.black};
    margin-bottom: ${theme.spacing(0.4)};
  `,
  stepItemContent: css`
    padding-left: ${theme.spacing(4)};
  `,
  stepItemLabelNumber: css`
    display: inline-block;
    width: ${theme.spacing(4)};
  `,
  step1Item: css`
    margin-bottom: 0;
  `,
  errorMessage: css`
    margin-bottom: ${theme.spacing(1)};
  `,
};

export const Step4ApplicationConfirm = () => {
  return (
    <EnrollmentStepPerformanceWrapper step={CURRENT_STEP}>
      <Step4ApplicationConfirmBody />
    </EnrollmentStepPerformanceWrapper>
  );
};

const Step4ApplicationConfirmBody = () => {
  const { t } = useCareCreditTranslations();

  const {
    locationId,
    multiStep: { goNextStep },
    enrollmentFormData,
    applicationResult,
    confirmationStepData,
    setConfirmationStepData,
    onConfirmApplication,
    isSubmitting,
    onGoBack,
  } = useCareCreditEnrollmentContext();

  const alerts = useAlert();

  const { fields } = enrollmentFormData;

  const { isComplete, getFieldProps } = useForm({
    fields: {
      agreeToTerms: { type: 'checkbox', required: true, value: confirmationStepData.agreeToTerms },
      applicationRevisionDate: {
        type: 'date',
        required: true,
        value: confirmationStepData.applicationRevisionDate,
      },
    },
  });

  const [hasPrinted, setHasPrinted] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const [errorState, setErrorState] = useState<{
    message: string;
  } | null>(null);

  const { getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const { fullPageApplication, isLoadingFullPageApplication, signaturePage, isLoadingSignaturePage } =
    useCareCreditPrefillApplicationQueries({
      locationId,
      enrollmentFormData,
    });

  const onClickSubmit = useCallback(async () => {
    setErrorState(null);
    setIsConfirming(true);
    try {
      const result = await onConfirmApplication({
        enrollmentResult: applicationResult,
      });

      const statusPayload = result.statusPayload;
      if (statusPayload && !statusPayload.errorResponse) {
        goNextStep({ orStep: NEXT_STEP });
      } else {
        if (result.message) {
          setErrorState({
            message: result.message,
          });
        } else {
          alerts.error(t('An unknown error occurred. Please try again.'));
        }
      }
    } catch (err) {
      if (err instanceof HttpError) {
        // if applicationError.message is json, parse it, otherwise use message.
        try {
          const errorData = JSON.parse(err.message);

          let message = errorData.message
            ? `${errorData.message}. Please try again.`
            : 'An error occurred while submitting your application. Please try again.';
          if (errorData?.code === 3) {
            message = 'Submission validation failed. Please review the form and try again.';
          }

          setErrorState({
            message,
          });
        } catch (err2) {
          if (err2 instanceof Error) {
            setErrorState({
              message: err2.message || 'An error occurred while submitting your application. Please try again.',
            });
          }
        }
      }
    } finally {
      setIsConfirming(false);
    }
  }, [onConfirmApplication, enrollmentFormData.fields, applicationResult, confirmationStepData, goNextStep]);

  const onClickSecondary = useCallback(() => {
    onGoBack();
  }, [onGoBack]);

  const onAnyFieldChange = (e?: FieldChangeEvent) => {
    if (e) {
      normalizeChangeHandler(({ name, value }) => {
        const fieldProps = getFieldProps(name as keyof EnrollmentConfirmationData);
        fieldProps.onChange(e);
        setConfirmationStepData((prev) => ({
          ...prev,
          [name]: value,
        }));
      })(e as CustomChangeEvent);
    }
  };

  const onPrintClick = useCallback(() => {
    if (!fullPageApplication?.document) {
      alerts.error(t('Unable to load application document. Please try again.'));
      return;
    }

    printPdfFromBase64(fullPageApplication.document);
    setHasPrinted(true);
  }, [fullPageApplication]);

  const onPrintSignatureClick = useCallback(() => {
    if (!signaturePage?.document) {
      alerts.error(t('Unable to load signature page document. Please try again.'));
      return;
    }

    setHasPrinted(true);
    printPdfFromBase64(signaturePage.document);
  }, [signaturePage]);

  const printPdfFromBase64 = (base64: string) => {
    // Decode and create blob
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    // Print when loaded
    iframe.onload = () => {
      iframe.contentWindow?.print();
      // Cleanup after print dialog closes
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    };
  };

  const getFullName = () => {
    return [fields.firstName, fields.middleName, fields.lastName]
      .map((n) => n.trim())
      .filter(Boolean)
      .join(' ');
  };

  const getMailingAddress = () => {
    const { address, address2, city, state, postalCode } = fields;

    const street = [address, address2]
      .map((n) => n.trim())
      .filter(Boolean)
      .join(' ');

    return `${street}
    ${city}${city && ', '} ${state} ${postalCode}`.trim();
  };

  const na = t('N/A');

  return (
    <EnrollmentBaseStep
      id={CURRENT_STEP}
      label={t('Application Confirmation')}
      footer={{
        primary: {
          children: t('Submit'),
          disabled: !isComplete || !hasPrinted || isSubmitting || isConfirming,
          loading: isSubmitting || isConfirming,
        },
        onClickPrimary: onClickSubmit,
        secondary: {
          children: t('Cancel Application'),
        },
        onClickSecondary,
      }}
    >
      <div css={styles.content}>
        <div>
          {t(
            'Review and submit the information below and obtain the customer’s signature to complete the full credit application.'
          )}
        </div>

        <div>
          <Heading level={3}>{t('Confirm Applicant Information')}</Heading>

          <div css={styles.row}>
            <Label label={t('Applicant Name')}>{getFullName() || na}</Label>
            <Label label={t('Date of Birth')}>
              {fields.dateOfBirth ? dayjs(fields.dateOfBirth).format('MM/DD/YYYY') : na}
            </Label>
            <Label label={t('Email Address')}>{fields.email || na}</Label>
          </div>

          <div css={styles.row}>
            <Label label={t('SSN/TIN')}>{fields.ssn || na}</Label>
            <Label label={t('Phone Number')}>{fields.phoneNumber || na}</Label>
            <Label label={t('Mailing Address')}>{getMailingAddress() || na}</Label>
          </div>

          <div css={styles.row}>
            <Label label={t('Housing Status')}>{getHousingTypeLabel(fields.housingStatus) || na}</Label>
            <Label label={t('Monthly Net Income')}>
              {fields.monthlyNetIncome ? currency(fields.monthlyNetIncome, { decimals: 0 }) : na}
            </Label>
          </div>

          <div css={styles.row}>
            <Label label={t('Primary ID Type')}>{getIdTypeLabel(fields.idType) || na}</Label>
            <Label label={t('Exp. Date')}>
              {fields.idExpirationDate ? dayjs(fields.idExpirationDate).format('MM/DD/YYYY') : na}
            </Label>
          </div>
        </div>

        <div className='signature-block' css={styles.signatureBlock}>
          <div css={styles.signatureBlockInner}>
            <Heading level={3}>{t('Obtain Applicant Signature')}</Heading>
            <div>
              {!hasPrinted && (
                <Chip variant='warn' maxWidth='unset'>
                  <div css={styles.signatureBlockChipContent}>
                    <span>
                      <Icon size={16} name='warning-badge-small' />
                    </span>
                    <span>{t('Action Required')}</span>
                  </div>
                </Chip>
              )}
            </div>
          </div>
          <Text>{t('Follow these steps to complete application')}</Text>

          <hr css={styles.divider} />

          <StepItem step='1' label={t('Print the Application')} css={styles.step1Item}>
            <Button
              {...getTriggerProps()}
              loading={isLoadingFullPageApplication || isLoadingSignaturePage}
              variant='tertiary'
              type='button'
              size='large'
              iconName='print'
            >
              {t('Print Application')}
              <span>
                <Icon size={16} name='caret-down' />
              </span>
            </Button>
            <PopoverMenu {...getMenuProps()}>
              <PopoverMenuItem {...getItemProps({ index: 0, onClick: onPrintClick })}>
                {t('Print Full Application')}
              </PopoverMenuItem>
              <PopoverMenuItem {...getItemProps({ index: 1, onClick: onPrintSignatureClick })}>
                {t('Print Signature Page')}
              </PopoverMenuItem>
            </PopoverMenu>
          </StepItem>

          <StepItem step='2' label={t('Have applicant sign the printed application')}>
            <Text>{t('Applicant must review and sign the physical copy.')}</Text>
          </StepItem>

          <StepItem step='3' label={t('Confirm and submit below')}>
            <Text>{t('Check the box below only after obtaining the physical signature')}</Text>
          </StepItem>
        </div>

        <div>
          <div css={styles.disclaimerBlock}>
            <CheckboxField
              {...getFieldProps('agreeToTerms')}
              label={t(
                'I certify that the application information was provided by the applicant. I have provided all required notices and statements, including a copy of the Synchrony Application which contains the Synchrony Account Agreement terms, to the applicant prior to submitting this application. I am submitting this application on behalf of the applicant based on the applicant having signed either (i) a printed version of this online application (in English or Spanish, as applicable) or (ii) a pre-printed paper copy of the Synchrony Application (in English or Spanish, as applicable).'
              )}
              onChange={onAnyFieldChange}
            />
          </div>
          <div css={styles.dateInput}>
            <DateField
              {...getFieldProps('applicationRevisionDate')}
              label={t('Application Revision Date')}
              onChange={onAnyFieldChange}
            />
          </div>
        </div>
      </div>

      {errorState && (
        <BannerNotification
          status='error'
          message={<>{errorState.message && <Text css={styles.errorMessage}>{errorState.message}</Text>}</>}
        />
      )}
    </EnrollmentBaseStep>
  );
};

const Label = ({ children, label }: { label: string; children?: React.ReactNode }) => {
  return (
    <div>
      <Text size='small' color='subdued'>
        {label}
      </Text>
      <Text size='large'>{children}</Text>
    </div>
  );
};

const StepItem = ({
  step,
  label,
  children,
  className,
}: React.PropsWithChildren<{ step: string; label: string; className?: string }>) => {
  return (
    <div className={className} css={styles.stepItem}>
      <Text size='large' weight='bold' css={styles.stepItemLabel}>
        <span css={styles.stepItemLabelNumber}>{step}. </span>
        {label}
      </Text>
      <div css={styles.stepItemContent}>{children}</div>
    </div>
  );
};
